import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
    AutoRefreshControl, useRefreshInterval, SLOW_REFRESH_INTERVALS,
} from '@/components/ui/auto-refresh-control'
import { useLocationTracking } from '../../hooks'
import { GlobalMap, type MapLocation } from '@/components/maps/GlobalMap'

/**
 * Where the recovery is happening.
 *
 * Its own tab rather than a panel above the clocks: the map answers a different question from
 * "what time is it there", and a 340px canvas on every visit to Location Tracking pushed the
 * engineer columns people actually read below the fold.
 *
 * It reads the same location-tracking payload as that screen, so the numbers on a marker and
 * the numbers in a column are the same numbers — not two counts of the same thing that can
 * drift apart.
 */

export function RecoveryMapTab({ setId }: { setId: number }) {
    const { t } = useTranslation()
    /**
     * The shared control, with the SLOW interval set. A map re-reads locations, engineers and
     * travel windows; polling that every ten seconds is waste, not attentiveness. One format
     * across the application, intervals chosen per screen.
     */
    const [refresh, setRefresh] = useRefreshInterval(
        'recovery-map.refresh', false, SLOW_REFRESH_INTERVALS)
    const refreshMs = refresh === false ? 0 : refresh

    const { data, isLoading, isError, refetch, isFetching } = useLocationTracking(setId, { refreshMs })

    /**
     * Tracked locations as markers.
     *
     * A location with no coordinates is counted in the footer, not plotted — the same rule the
     * estate map uses, so a set that includes SaaS or an unassigned bucket says so rather than
     * quietly showing fewer objects than the tab beside it.
     */
    const mapLocations: MapLocation[] = useMemo(
        () => (data?.locations ?? []).map((l: any) => ({
            id: String(l.id),
            name: l.description || l.name || 'Unknown',
            coords: (l.latitude != null && l.longitude != null)
                ? [Number(l.longitude), Number(l.latitude)] as [number, number]
                : null,
            category: l.category || '',
            place: l.name || '',
            objects: Number(l.objectCount || 0),
            engineers: Number(l.assignedEngineerCount || 0),
            earliestStart: l.earliestStart ?? null,
            highestRta: l.highestRta ?? null,
            // This payload has carried an IANA zone all along — it is what drives the clocks on
            // the Location Tracking tab. The map's hover clock reads the same field, so the two
            // surfaces cannot show different times for the same place.
            timeZone: l.timeZone ?? null,
        })),
        [data]
    )

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold">
                        {t('recoverRequests.recoveryMap.title', 'Recovery map')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        {t('recoverRequests.recoveryMap.description', 'Where this recovery set is being worked, with objects, engineers and the recovery window per location.')}
                    </p>
                </div>
                <AutoRefreshControl
                    value={refresh}
                    onChange={setRefresh}
                    onRefresh={() => void refetch()}
                    isRefreshing={isFetching}
                    intervals={SLOW_REFRESH_INTERVALS}
                />
            </div>

            {isLoading ? (
                <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
                    {t('common.loading', 'Loading…')}
                </div>
            ) : isError ? (
                <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-6 text-center text-sm text-destructive">
                    {t('recoverRequests.locationTracking.error', 'Could not load location tracking data.')}
                </div>
            ) : mapLocations.length === 0 ? (
                <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                    {t('recoverRequests.locationTracking.empty', 'No recovery locations for this set.')}
                </div>
            ) : (
                <div className="rounded-lg border bg-card p-3">
                    <GlobalMap locations={mapLocations} height={520} />
                </div>
            )}
        </div>
    )
}
